import { useEffect, useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/stores/authStore'
import {
  isPushConfigured,
  isNotificationSupported,
  requestAdminFcmToken,
  subscribeForegroundMessages,
  showBrowserNotification,
} from '@/lib/messaging'
import { saveAdminFcmToken } from '@/services/admin/fcmTokens'

export function useAdminPushNotifications() {
  const user = useAuthStore((s) => s.user)
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const [enabled, setEnabled] = useState(false)
  const [loading, setLoading] = useState(false)

  const enablePush = useCallback(async () => {
    if (!user?.uid || !isPushConfigured()) {
      toast.error(
        'Push is not configured. Add VITE_FIREBASE_VAPID_KEY to .env and redeploy.'
      )
      return false
    }

    setLoading(true)
    try {
      const token = await requestAdminFcmToken()
      if (!token) {
        toast.error('Notification permission was denied or unavailable')
        return false
      }
      await saveAdminFcmToken(user.uid, user.email || '', token)
      setEnabled(true)
      toast.success('Order alerts enabled on this device')

      await showBrowserNotification(
        'Bazm-e-Musk Admin',
        'You will receive alerts when new orders are placed.',
        {}
      )
      return true
    } catch (err) {
      console.error('enablePush failed', err)
      toast.error(
        err instanceof Error ? err.message : 'Could not enable notifications'
      )
      return false
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (!isAdmin || !user) return

    let unsubscribe: (() => void) | undefined

    void subscribeForegroundMessages((payload) => {
      const title = payload.title || 'New order placed'
      const body = payload.body || ''
      toast.success(body ? `${title} — ${body}` : title, { duration: 8000 })
    }).then((unsub) => {
      unsubscribe = unsub ?? undefined
    })

    return () => unsubscribe?.()
  }, [isAdmin, user])

  useEffect(() => {
    if (!isAdmin || !user || !isPushConfigured() || !isNotificationSupported()) {
      return
    }
    if (Notification.permission !== 'granted') return

    void (async () => {
      try {
        const token = await requestAdminFcmToken()
        if (token) {
          await saveAdminFcmToken(user.uid, user.email || '', token)
          setEnabled(true)
        }
      } catch (err) {
        console.warn('Auto FCM token refresh failed', err)
      }
    })()
  }, [isAdmin, user])

  return { enabled, loading, enablePush, isConfigured: isPushConfigured() }
}
